import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import SendIcon from "@mui/icons-material/Send";
import {
  isAiAssistantEnabled,
  useAssistantChat,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  FLOATING_ACTIONS_RIGHT,
  TETA_FAB_BOTTOM_PX,
  TETA_FAB_COLOR,
  TETA_FAB_COLOR_HOVER,
  TETA_FAB_ICON_COLOR
} from "../../constants/floatingActionsLayout";
import {
  hasTetaAutoPopupOccurred,
  markTetaAutoPopupOccurred
} from "../../Utils/embeddedShellSession";
import {
  clearTetaSessionCookie,
  getOrCreateTetaSessionId
} from "../../Utils/tetaChatSession";
import TetaMessageContent from "./TetaMessageContent";
import { trackClarityEvent } from "../../analytics/clarity";
import { trackClarityApiCall } from "../../analytics/trackClarityApi";

const DEFAULT_TETA_NUDGE_DELAY_SECONDS = 45;

function getTetaNudgeDelayMs(): number {
  const raw = process.env.REACT_APP_TETA_NUDGE_DELAY_SECONDS?.trim();
  if (!raw) {
    return DEFAULT_TETA_NUDGE_DELAY_SECONDS * 1000;
  }
  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return DEFAULT_TETA_NUDGE_DELAY_SECONDS * 1000;
  }
  return seconds * 1000;
}

function playTetaNudgeChime(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  try {
    const AudioContextCtor =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }

    const ctx = new AudioContextCtor();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(
      660,
      ctx.currentTime + 0.18
    );
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.07, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);

    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.22);

    window.setTimeout(() => {
      void ctx.close();
    }, 300);
  } catch {
    /* Autoplay or audio unsupported */
  }
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

/** Gate feature flag without mounting chat hooks (avoids session cookie when disabled). */
const TetaAssistant: React.FC = () => {
  if (!isAiAssistantEnabled()) {
    return null;
  }
  return <TetaAssistantPanel />;
};

const TetaAssistantPanel: React.FC = () => {
  const location = useLocation();
  const isPortal = location.pathname.includes("/portal");
  const { t } = useKivunovaTranslation();
  const { sendMessage, isPending } = useAssistantChat();
  const tetaLabel = t("teta-assistant-label", "Ask Teta");
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [showNudge, setShowNudge] = useState(false);

  const nudgeMessage = isPortal
    ? t(
        "teta-assistant-nudge-portal",
        "Need a hand? Ask me about stock, invoices, or products."
      )
    : t(
        "teta-assistant-nudge-public",
        "Questions about Kivunova FMS? I'm here to help — tap to chat."
      );

  useEffect(() => {
    if (hasTetaAutoPopupOccurred()) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (hasTetaAutoPopupOccurred()) {
        return;
      }
      markTetaAutoPopupOccurred();
      setShowNudge(true);
      playTetaNudgeChime();
    }, getTetaNudgeDelayMs());

    return () => window.clearTimeout(timer);
  }, []);

  const ensureSessionId = useCallback((): string => {
    if (sessionId) {
      return sessionId;
    }
    const id = getOrCreateTetaSessionId();
    setSessionId(id);
    return id;
  }, [sessionId]);

  const handleOpen = useCallback(() => {
    markTetaAutoPopupOccurred();
    setShowNudge(false);
    setOpen(true);
    ensureSessionId();
    trackClarityEvent("assistant_opened");
  }, [ensureSessionId]);

  const handleDismissNudge = useCallback(() => {
    setShowNudge(false);
  }, []);

  const handleMinimize = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInput("");
    clearTetaSessionCookie();
    setSessionId(undefined);
    setMessages([]);
    setErrorText(null);
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isPending) return;

    const activeSessionId = ensureSessionId();

    setInput("");
    setErrorText(null);
    setMessages((prev) => [...prev, { role: "user", content: text }]);

    try {
      const result = await sendMessage({
        message: text,
        sessionId: activeSessionId,
        context: window.location.pathname
      });
      trackClarityApiCall({
        query: "mutation AssistantChat { assistantChat { code } }",
        outcome: "success",
        code: "OK"
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            result.reply || t("teta-assistant-no-response", "(No response)")
        }
      ]);
    } catch (err) {
      const code =
        err instanceof Error && err.message === "FEATURE_DISABLED"
          ? "FEATURE_DISABLED"
          : "ASSISTANT_ERROR";
      trackClarityApiCall({
        query: "mutation AssistantChat { assistantChat { code } }",
        outcome:
          code === "FEATURE_DISABLED" ? "business_failure" : "graphql_error",
        code
      });
      const msg =
        err instanceof Error && err.message === "FEATURE_DISABLED"
          ? t(
              "teta-assistant-error-disabled",
              "Teta is not enabled for this environment."
            )
          : t(
              "teta-assistant-error-unreachable",
              "Could not reach Teta. Try again later."
            );
      setErrorText(msg);
    }
  }, [input, isPending, sendMessage, ensureSessionId, t]);

  const floatingAnchorSx = {
    position: "fixed" as const,
    bottom: TETA_FAB_BOTTOM_PX,
    right: FLOATING_ACTIONS_RIGHT,
    zIndex: 1300
  };

  const tetaFabSx = {
    bgcolor: TETA_FAB_COLOR,
    color: TETA_FAB_ICON_COLOR,
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.22)",
    animation: "tetaFabPop 2.2s ease-in-out infinite",
    "@keyframes tetaFabPop": {
      "0%, 100%": {
        transform: "scale(1)",
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.22)"
      },
      "45%": {
        transform: "scale(1.12)",
        boxShadow: "0 10px 28px rgba(255, 193, 7, 0.7)"
      },
      "55%": {
        transform: "scale(1.06)",
        boxShadow: "0 8px 22px rgba(255, 193, 7, 0.55)"
      }
    },
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none"
    },
    "&:hover": {
      bgcolor: TETA_FAB_COLOR_HOVER,
      animation: "none",
      transform: "scale(1.08)",
      boxShadow: "0 8px 22px rgba(255, 193, 7, 0.55)"
    }
  };

  return (
    <>
      {!open && (
        <Box
          sx={{
            ...floatingAnchorSx,
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row-reverse" },
            alignItems: { xs: "flex-end", sm: "center" },
            gap: 1.5,
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" }
          }}
        >
          {showNudge && (
            <Paper
              elevation={8}
              role="status"
              aria-live="polite"
              onClick={handleOpen}
              sx={{
                position: "relative",
                maxWidth: { xs: "min(280px, calc(100vw - 88px))", sm: 260 },
                px: 1.5,
                py: 1.25,
                pr: 4,
                borderRadius: 2,
                borderLeft: 4,
                borderLeftColor: TETA_FAB_COLOR,
                boxShadow: "0 8px 28px rgba(0, 0, 0, 0.18)",
                cursor: "pointer",
                animation: "tetaNudgeIn 0.4s ease-out",
                "@keyframes tetaNudgeIn": {
                  from: {
                    opacity: 0,
                    transform: "translateX(12px)"
                  },
                  to: {
                    opacity: 1,
                    transform: "translateX(0)"
                  }
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none"
                },
                "&:hover": {
                  boxShadow: "0 8px 24px rgba(255, 193, 7, 0.35)"
                }
              }}
            >
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDismissNudge();
                }}
                aria-label={t("teta-assistant-nudge-dismiss", "Dismiss")}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  color: "text.secondary"
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <SmartToyOutlinedIcon
                  sx={{ color: TETA_FAB_COLOR, fontSize: 20, mt: 0.25 }}
                  aria-hidden
                />
                <Typography variant="body2" fontWeight={500} lineHeight={1.45}>
                  {nudgeMessage}
                </Typography>
              </Box>
            </Paper>
          )}

          <Tooltip
            title={tetaLabel}
            placement="left"
            arrow
            disableHoverListener={showNudge}
          >
            <Fab
              aria-label={tetaLabel}
              onClick={handleOpen}
              sx={{
                ...tetaFabSx,
                ...(showNudge && {
                  animation: "tetaFabNudgePulse 1.6s ease-in-out infinite",
                  "@keyframes tetaFabNudgePulse": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      boxShadow: "0 4px 14px rgba(0, 0, 0, 0.22)"
                    },
                    "50%": {
                      transform: "scale(1.1)",
                      boxShadow: "0 10px 28px rgba(255, 193, 7, 0.75)"
                    }
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none"
                  }
                })
              }}
            >
              <SmartToyOutlinedIcon />
            </Fab>
          </Tooltip>
        </Box>
      )}

      {open && (
        <Paper
          elevation={8}
          sx={{
            ...floatingAnchorSx,
            width: { xs: "calc(100% - 32px)", sm: 380 },
            maxWidth: 380,
            height: 480,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyOutlinedIcon
                sx={{ color: TETA_FAB_COLOR, fontSize: 22 }}
                aria-hidden
              />
              <Typography variant="subtitle1" fontWeight={600}>
                {tetaLabel}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={handleMinimize}
                aria-label={t("teta-assistant-minimize", "Minimize")}
              >
                <MinimizeIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClose}
                aria-label={t("teta-assistant-close", "Close")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              px: 2,
              py: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {messages.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {isPortal
                  ? t(
                      "teta-assistant-empty-portal",
                      'Ask Teta can help with low stock, invoices, and product questions for your store. Example: "What products are low on stock?"'
                    )
                  : t(
                      "teta-assistant-empty-public",
                      "Ask Teta can answer questions about Kivunova FMS. Log in to ask about your stock, invoices, or orders."
                    )}
              </Typography>
            )}
            {messages.map((m, i) => (
              <Box
                key={`${m.role}-${i}`}
                sx={{
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                  bgcolor: m.role === "user" ? "primary.main" : "grey.100",
                  color:
                    m.role === "user" ? "primary.contrastText" : "text.primary",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2
                }}
              >
                {m.role === "assistant" ? (
                  <TetaMessageContent content={m.content} />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {m.content}
                  </Typography>
                )}
              </Box>
            ))}
            {isPending && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {errorText && (
              <Typography variant="body2" color="error">
                {errorText}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: 1,
              borderColor: "divider"
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={t("teta-assistant-placeholder", "Message Teta...")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              disabled={isPending}
              multiline
              maxRows={3}
            />
            <IconButton
              color="primary"
              onClick={() => void handleSend()}
              disabled={isPending || !input.trim()}
              aria-label={t("teta-assistant-send", "Send")}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default TetaAssistant;
